"use client"

import * as React from "react"
import { Select as SelectPrimitive } from "@base-ui/react/select"
import { ChevronDownIcon, CheckIcon, ChevronUpIcon, ChevronsUpDownIcon } from "lucide-react"
import { actionSheet } from "@/design-system/action-sheet.ts"
import { BottomSheetHandle } from "@/primitives/drawer"
import { useBackDismiss } from "@/design-system/use-back-dismiss.ts"
import { useOverlayForm } from "@/design-system/use-overlay-form.ts"
import { cn } from "@/primitives/utils"

/*
 * beacon: a Select chooses its form as it opens (`useOverlayForm`) and keeps it while open: its list
 * below the trigger on the desk, and on `compact` docked as an action sheet (`action-sheet.ts`) over
 * the scrim, which Back closes. Base UI's Select has no imperative close, so the root always holds
 * whether it is open, and Back closes it there, telling the caller through `onOpenChange`.
 */
const SelectDockedContext = React.createContext(false)

/** beacon: the details a close on Back reports, where Base UI has no event of its own to give. */
const closedByBack = () =>
  ({
    reason: "none",
    event: new Event("popstate"),
    cancel: () => {},
    allowPropagation: () => {},
    isCanceled: false,
    isPropagationAllowed: false,
    trigger: undefined,
  }) as unknown as SelectPrimitive.Root.ChangeEventDetails

function Select<Value, Multiple extends boolean | undefined = false>(
  props: SelectPrimitive.Root.Props<Value, Multiple>,
) {
  const { open, docked, onOpenChange } = useOverlayForm(props)
  useBackDismiss(open && docked, () => onOpenChange(false, closedByBack()))
  return (
    <SelectDockedContext value={docked}>
      <SelectPrimitive.Root
        {...props}
        defaultOpen={undefined}
        open={open}
        onOpenChange={onOpenChange}
      />
    </SelectDockedContext>
  )
}

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      // beacon: the popup carries the padding.
      className={cn("scroll-my-1", className)}
      {...props}
    />
  )
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn("flex flex-1 text-left", className)}
      {...props}
    />
  )
}

function SelectTrigger({
  className,
  size = "default",
  variant = "default",
  children,
  ...props
}: SelectPrimitive.Trigger.Props & {
  size?: "sm" | "default"
  variant?: "default" | "ghost"
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      data-variant={variant}
      className={cn(
        // beacon: the Input's metrics and states (32px, 28px at sm; taller on touch, 44 and 36, the
        // smaller reaching 44 with `touch-target`), the value in the foreground and the placeholder
        // subtle, behind an up-down chevron. It
        // takes the width it is given. `ghost` has no chrome until hovered, for a select inside a
        // row or a Display menu.
        "flex w-fit items-center justify-between gap-1.5 rounded-md border border-input bg-background px-2.5 text-ui whitespace-nowrap text-foreground transition-[color,background-color,border-color,box-shadow] duration-100 ease-snappy outline-none hover:border-border-strong focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/20 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/15 data-placeholder:text-subtle-foreground data-popup-open:border-ring data-[size=default]:h-8 data-[size=sm]:h-7 data-[size=sm]:px-2 touch:data-[size=default]:min-h-11 touch:data-[size=sm]:min-h-9 touch-target *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5 dark:bg-surface-sunken [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "data-[variant=ghost]:border-transparent data-[variant=ghost]:bg-transparent data-[variant=ghost]:shadow-none data-[variant=ghost]:hover:border-input data-[variant=ghost]:hover:bg-accent data-[variant=ghost]:data-popup-open:border-input data-[variant=ghost]:data-popup-open:bg-selected dark:data-[variant=ghost]:bg-transparent",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        render={
          <ChevronsUpDownIcon className="pointer-events-none size-3.5 text-subtle-foreground" />
        }
      />
    </SelectPrimitive.Trigger>
  )
}

function SelectContent({
  className,
  children,
  side = "bottom",
  sideOffset = 4,
  // beacon: the list opens below the trigger, lined up with its start edge, rather than over it,
  // like every other popup.
  align = "start",
  alignOffset = 0,
  alignItemWithTrigger = false,
  title,
  ...props
}: Omit<SelectPrimitive.Popup.Props, "title"> &
  Pick<
    SelectPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset" | "alignItemWithTrigger"
  > & {
    /** beacon: what the list chooses, as "Range": docked, the sheet's title under its handle. */
    title?: string
  }) {
  const docked = React.use(SelectDockedContext)
  const titleId = React.useId()
  const titled = docked && title !== undefined
  return (
    <SelectPrimitive.Portal>
      {docked && <SelectPrimitive.Backdrop className={actionSheet.backdrop} />}
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        // beacon: docked, the list never stands over its trigger.
        alignItemWithTrigger={alignItemWithTrigger && !docked}
        className={cn("isolate z-50", docked && actionSheet.positioner)}
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          data-align-trigger={alignItemWithTrigger && !docked}
          data-docked={docked || undefined}
          data-bottom-sheet={docked || undefined}
          className={cn(
            docked
              ? // beacon: an action sheet.
                cn(actionSheet.popup, "relative isolate")
              : // beacon: the shared popup: at least the trigger's width and otherwise its
                // content's, up to 28rem, lifted by the popover shadow (whose hairline replaces the
                // ring).
                "relative isolate z-50 max-h-(--available-height) w-max max-w-md min-w-(--anchor-width) origin-(--transform-origin) overflow-x-hidden overflow-y-auto rounded-lg bg-popover p-1 text-ui text-popover-foreground shadow-popover duration-150 ease-snappy data-[align-trigger=true]:animate-none data-[side=bottom]:slide-in-from-top-2 data-[side=inline-end]:slide-in-from-left-2 data-[side=inline-start]:slide-in-from-right-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-[0.98] data-closed:animate-out data-closed:duration-100 data-closed:fade-out-0 data-closed:zoom-out-[0.98]",
            className,
            // beacon: a caller's width gives way to the sheet's.
            docked && "w-full max-w-none min-w-0",
          )}
          {...(titled && { "aria-labelledby": titleId })}
          {...props}
        >
          {/* beacon: docked, under the handle every bottom sheet carries, then its title. */}
          {docked && <BottomSheetHandle className={actionSheet.handle} />}
          {titled && (
            <p
              id={titleId}
              className="px-2 pt-1 pb-2 font-heading text-title font-semibold text-foreground"
            >
              {title}
            </p>
          )}
          <SelectScrollUpButton />
          <SelectPrimitive.List>{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  )
}

function SelectLabel({ className, ...props }: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      // beacon: a group's label is tertiary meta text.
      className={cn("px-2 py-1.5 text-xs font-medium text-subtle-foreground", className)}
      {...props}
    />
  )
}

function SelectItem({ className, children, ...props }: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        // beacon: 32px rows (44px on touch) in UI text, highlighted in the popover's accent (the
        // panel's would vanish on a dark popover), with the check in a 16px slot on the left so
        // labels line up whether or not they're chosen.
        "relative flex h-8 w-full touch:min-h-11 cursor-default items-center gap-2 rounded-md pr-2 pl-7 text-ui outline-hidden select-none focus:bg-popover-accent focus:text-accent-foreground data-highlighted:bg-popover-accent data-highlighted:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        // beacon: on touch, docked as an action sheet, the chosen option is marked by its check
        // alone: the focus and highlight Base UI give it as the list opens take no fill, and a press
        // fills the row as a highlight would.
        "touch:in-data-bottom-sheet:focus:not-active:bg-transparent touch:in-data-bottom-sheet:focus:not-active:text-inherit touch:in-data-bottom-sheet:data-highlighted:not-active:bg-transparent touch:in-data-bottom-sheet:data-highlighted:not-active:text-inherit touch:in-data-bottom-sheet:active:bg-popover-accent touch:in-data-bottom-sheet:active:text-accent-foreground",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute left-2 flex size-4 items-center justify-center" />
        }
      >
        <CheckIcon className="pointer-events-none size-3.5" />
      </SelectPrimitive.ItemIndicator>
      <SelectPrimitive.ItemText className="flex min-w-0 flex-1 shrink-0 items-center gap-2 truncate whitespace-nowrap">
        {children}
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
}

function SelectSeparator({ className, ...props }: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      // beacon: the subtle hairline.
      className={cn("pointer-events-none -mx-1 my-1 h-px bg-border-subtle", className)}
      {...props}
    />
  )
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        "top-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-3.5",
        className,
      )}
      {...props}
    >
      <ChevronUpIcon />
    </SelectPrimitive.ScrollUpArrow>
  )
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-3.5",
        className,
      )}
      {...props}
    >
      <ChevronDownIcon />
    </SelectPrimitive.ScrollDownArrow>
  )
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
